import { useEffect, useState, useRef, useCallback } from 'react';
import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';

function calculateHandOpenness(landmarks) {
  const palm = landmarks[9];
  const thumbTip = landmarks[4];
  const indexTip = landmarks[8];
  const middleTip = landmarks[12];
  const ringTip = landmarks[16];
  const pinkyTip = landmarks[20];
  const wrist = landmarks[0];

  const distances = [
    Math.sqrt(Math.pow(thumbTip.x - palm.x, 2) + Math.pow(thumbTip.y - palm.y, 2) + Math.pow(thumbTip.z - palm.z, 2)),
    Math.sqrt(Math.pow(indexTip.x - palm.x, 2) + Math.pow(indexTip.y - palm.y, 2) + Math.pow(indexTip.z - palm.z, 2)),
    Math.sqrt(Math.pow(middleTip.x - palm.x, 2) + Math.pow(middleTip.y - palm.y, 2) + Math.pow(middleTip.z - palm.z, 2)),
    Math.sqrt(Math.pow(ringTip.x - palm.x, 2) + Math.pow(ringTip.y - palm.y, 2) + Math.pow(ringTip.z - palm.z, 2)),
    Math.sqrt(Math.pow(pinkyTip.x - palm.x, 2) + Math.pow(pinkyTip.y - palm.y, 2) + Math.pow(pinkyTip.z - palm.z, 2))
  ];

  const palmSize = Math.sqrt(Math.pow(wrist.x - palm.x, 2) + Math.pow(wrist.y - palm.y, 2) + Math.pow(wrist.z - palm.z, 2));
  const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length;
  const normalizedDistance = avgDistance / (palmSize * 2);

  return Math.min(1, Math.max(0, normalizedDistance * 2));
}

export function useHandTracking({ onStatusChange }) {
  const [handScale, setHandScale] = useState(1.0);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [gestureState, setGestureState] = useState({
    leftDetected: false,
    rightDetected: false,
    leftOpenness: 0,
    rightOpenness: 0,
    avgOpenness: 0,
    scale: 1.0
  });
  
  const handLandmarkerRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const isDetectionActiveRef = useRef(false);
  const animationFrameRef = useRef(null);
  const lastTimestampRef = useRef(0);

  const processFrame = useCallback(async () => {
    if (!isDetectionActiveRef.current || !handLandmarkerRef.current || !videoRef.current) {
      return;
    }

    const video = videoRef.current;
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const timestamp = Date.now();
      const results = handLandmarkerRef.current.detectForVideo(video, timestamp);

      if (results.landmarks && results.landmarks.length > 0) {
        let leftHandDetected = false;
        let rightHandDetected = false;
        let leftOpenness = 0;
        let rightOpenness = 0;

        results.landmarks.forEach((landmarks, index) => {
          // The new API returns landmarks as arrays of {x, y, z} objects
          // They're already in the correct format for calculateHandOpenness
          const handedness = results.handednesses?.[index]?.[0];
          const handednessLabel = handedness?.displayName || handedness?.categoryName || 'Unknown';
          
          // MediaPipe returns handedness from the model's perspective
          // When facing the camera: MediaPipe's "Left" = User's "Right", MediaPipe's "Right" = User's "Left"
          // So we flip: MediaPipe's "Left" means it's the user's right hand
          const isLeft = handednessLabel === "Left";
          const openness = calculateHandOpenness(landmarks);

          if (isLeft) {
            leftHandDetected = true;
            leftOpenness = openness;
          } else {
            rightHandDetected = true;
            rightOpenness = openness;
          }
        });

        const avgOpenness = (leftHandDetected && rightHandDetected)
          ? (leftOpenness + rightOpenness) / 2
          : (leftHandDetected ? leftOpenness : rightOpenness);

        const targetScale = 0.3 + avgOpenness * 2.2;

        const newState = {
          leftDetected: leftHandDetected,
          rightDetected: rightHandDetected,
          leftOpenness,
          rightOpenness,
          avgOpenness,
          scale: targetScale
        };
        
        setGestureState(newState);
        window.gestureState = newState; // Expose for GesturePanel
        setHandScale(targetScale);
        onStatusChange?.({ type: 'connected', text: leftHandDetected || rightHandDetected ? 'Hands detected' : 'Detecting...' });
      } else {
        const resetState = {
          leftDetected: false,
          rightDetected: false,
          leftOpenness: 0,
          rightOpenness: 0,
          avgOpenness: 0,
          scale: 1.0
        };
        setHandScale(1.0);
        setGestureState(resetState);
        window.gestureState = resetState; // Expose for GesturePanel
        onStatusChange?.({ type: 'connected', text: 'Waiting for hands...' });
      }
    }

    if (isDetectionActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(processFrame);
    }
  }, [onStatusChange]);

  const initializeHandTracking = useCallback(async () => {
    try {
      onStatusChange?.({ type: 'connected', text: 'Loading MediaPipe...' });

      // Initialize the MediaPipe Vision Tasks
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm"
      );

      // Create HandLandmarker
      const handLandmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: `https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task`,
          delegate: "GPU"
        },
        numHands: 2,
        runningMode: "VIDEO",
        minHandDetectionConfidence: 0.7,
        minHandPresenceConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      handLandmarkerRef.current = handLandmarker;

      // Create video element
      const video = document.createElement('video');
      video.style.display = 'none';
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      document.body.appendChild(video);
      videoRef.current = video;

      isDetectionActiveRef.current = false;

      // Expose startCamera function
      window.startCamera = async function() {
        if (!isDetectionActiveRef.current) {
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            onStatusChange?.({ type: 'error', text: 'Camera API not available. This site requires HTTPS.' });
            return;
          }

          isDetectionActiveRef.current = true;
          onStatusChange?.({ type: 'connected', text: 'Requesting camera access...' });

          try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
              video: { width: 640, height: 480, facingMode: 'user' } 
            });
            
            streamRef.current = stream;
            video.srcObject = stream;
            video.play();

            // Start processing frames
            animationFrameRef.current = requestAnimationFrame(processFrame);
            
            setIsCameraActive(true);
            window.isCameraActive = true;
            onStatusChange?.({ type: 'connected', text: 'Camera active - Show your hands!' });
          } catch (error) {
            console.error('Camera error:', error);
            let errorMsg = 'Camera access denied';
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
              errorMsg = 'Camera permission denied. Please allow camera access in browser settings.';
            } else if (error.name === 'NotFoundError') {
              errorMsg = 'No camera found. Please connect a camera.';
            } else if (error.name === 'NotReadableError') {
              errorMsg = 'Camera is already in use by another application.';
            }
            onStatusChange?.({ type: 'error', text: errorMsg });
            isDetectionActiveRef.current = false;
          }
        }
      };

      // Expose stopCamera function
      window.stopCamera = function() {
        if (isDetectionActiveRef.current) {
          isDetectionActiveRef.current = false;
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          setIsCameraActive(false);
          window.isCameraActive = false;
          onStatusChange?.({ type: 'connected', text: 'Camera stopped' });
        }
      };
    } catch (error) {
      console.error('Hand tracking initialization error:', error);
          onStatusChange?.({ type: 'error', text: 'Failed to initialize hand tracking: ' + error.message });
    }
  }, [onStatusChange, processFrame]);

  useEffect(() => {
    initializeHandTracking().then(() => {
      // Automatically start camera after initialization
      setTimeout(() => {
        window.startCamera?.();
      }, 500); // Small delay to ensure everything is ready
    });

    return () => {
      // Cleanup
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (videoRef.current) {
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject = null;
        }
        if (videoRef.current.parentNode) {
          videoRef.current.parentNode.removeChild(videoRef.current);
        }
      }
    };
  }, [initializeHandTracking]);

  // Expose gestureState globally for GesturePanel component
  useEffect(() => {
    window.gestureState = gestureState;
  }, [gestureState]);

  // Expose camera status globally for components
  useEffect(() => {
    window.isCameraActive = isCameraActive;
    // Trigger a custom event so components can listen for changes
    window.dispatchEvent(new CustomEvent('cameraStatusChange', { detail: { isActive: isCameraActive } }));
  }, [isCameraActive]);

  return { handScale, gestureState, isCameraActive };
}
